import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import i18n from '../i18n';
import { esquecerIdiomaDaSessao, normalizarIdioma } from '../i18n/languageCascade';
import api from '../services/api';
import axios from 'axios';
import { API_URL } from '../config';

// Interface para o objeto de usuário
interface UserContext {
  Nome: string;
  Role: string;
  userId: number;
  userEmail: string;
  userTenant?: number; // ✅ ADICIONADO: Campo userTenant
  /**
   * Parceiro do usuário. 0 = grupo raiz.
   *
   * Pode vir ausente enquanto o backend não devolver o campo; nesse caso o
   * resolvedor de escopo (hooks/useScope) assume o grupo raiz, preservando o
   * comportamento atual.
   */
  /**
   * Parceiro do usuário.
   * Chave AUSENTE = o backend não informou (desconhecido).
   * `null` = super admin · `0` = operação principal · `>0` = parceiro terceiro.
   */
  userPartner?: number | null;
  /** Idioma escolhido pelo usuário — o mais específico da cascata. */
  userLanguage?: string | null;
}

// ✅ NOVA: Interface para response do login
interface LoginResponse {
  Nome: string;
  Role: string;
  token: string;
  refreshToken?: string;
  userId: number;
  userTenant?: number; // ✅ ADICIONADO: Campo userTenant no response
  userPartner?: number | null;
  userLanguage?: string | null;
  [key: string]: any; // Para outros campos que possam vir
}

// Interface para o contexto de autenticação
interface AuthContextType {
  user: UserContext | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<LoginResponse>; // ✅ MUDADO: Agora retorna LoginResponse
  logout: () => void;
}

// Criação do contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook personalizado para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

// Props para o provider
interface AuthProviderProps {
  children: ReactNode;
}

// ✅ UTILITÁRIO MELHORADO - Verificação mais detalhada
const isTokenValid = (token: string): boolean => {
  try {
    if (!token || token.trim().length === 0) {
      console.log('🔐 Token vazio ou nulo');
      return false;
    }

    const trimmedToken = token.trim();

    // ✅ VERIFICAÇÃO JWT MELHORADA
    try {
      const parts = trimmedToken.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        const now = Math.floor(Date.now() / 1000);
        
        if (payload.exp) {
          const timeLeft = payload.exp - now;
          const minutesLeft = Math.floor(timeLeft / 60);
          
          console.log(`🔐 Token JWT - Tempo restante: ${minutesLeft} minutos`);
          
          if (payload.exp < now) {
            console.log('🔐 Token JWT expirado');
            return false;
          }
        }
      }
    } catch (jwtError) {
      // Não é JWT, mas pode ser um token válido
      console.log('🔐 Token não é JWT, mas pode ser válido');
    }

    const isValid = trimmedToken.length > 0;
    console.log(isValid ? '✅ Bearer Token presente' : '❌ Bearer Token inválido');
    return isValid;
  } catch (error) {
    console.error('❌ Erro ao verificar Bearer Token:', error);
    return false;
  }
};

// Utilitário para limpar todos os dados de autenticação
const clearAllAuthData = (): void => {
  console.log('🧹 Limpando todos os dados de autenticação...');

  // Limpar localStorage
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('authToken');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('selectedTenantId'); // ✅ ADICIONADO: Limpar tenant selecionado

  // Limpar dados de navegação criptografada
  localStorage.removeItem('n0-encrypted-state');
  localStorage.removeItem('n0-shareable-url');

  // Limpar sessionStorage
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('authToken');

  // O idioma da sessão morre com ela. Mantê-lo faria a conta seguinte abrir
  // no idioma da anterior — e a tela de login ficaria com o resto da última
  // pessoa que usou o navegador.
  esquecerIdiomaDaSessao();

  console.log('✅ Todos os dados de autenticação foram limpos');
};

// Provider que gerencia o estado de autenticação
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserContext | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Ref para controlar se já estamos fazendo refresh
  const isRefreshing = useRef(false);
  // ✅ ADICIONAR: Controle de logout para evitar múltiplas chamadas
  const isLoggingOut = useRef(false);
  
  // Fila de requisições pendentes durante o refresh
  const failedQueue = useRef<Array<{
    resolve: (token: string) => void;
    reject: (error: any) => void;
  }>>([]);

  // Função para processar a fila de requisições pendentes
  const processQueue = useCallback((error: any, token: string | null = null) => {
    failedQueue.current.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token!);
      }
    });

    failedQueue.current = [];
  }, []);

  // ✅ FUNÇÃO DE RENOVAÇÃO CORRIGIDA
  const refreshAccessToken = useCallback(async (): Promise<string> => {
  console.log("🔄 Tentando renovar token...");

  const currentRefreshToken = localStorage.getItem("refreshToken");

  if (!currentRefreshToken) {
    throw new Error("Refresh token não encontrado");
  }

  const response = await axios.get(`${API_URL}/user/rtoken`, {
    headers: {
      Authorization: `Bearer ${currentRefreshToken}`,
    },
  });

  const newToken = response.data?.token;
  const newRefreshToken = response.data?.refreshToken;

  if (!newToken) {
    throw new Error("Novo token não recebido");
  }

  setToken(newToken);
  localStorage.setItem("token", newToken);

  if (newRefreshToken) {
    setRefreshToken(newRefreshToken);
    localStorage.setItem("refreshToken", newRefreshToken);
  }

  console.log("✅ Token renovado com sucesso");

  return newToken;
}, []);

  // Função para verificar se o usuário já está logado
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🔐 Inicializando verificação de autenticação...');
      setIsLoading(true);

      try {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        const storedRefreshToken = localStorage.getItem('refreshToken');

        // Se não há dados básicos, não está autenticado
        if (!storedUser || !storedToken) {
          console.log('ℹ️ Nenhum dado de autenticação encontrado');
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        // Verificar se o token é válido
        let validToken = storedToken;

if (!isTokenValid(storedToken)) {
  console.log("⚠️ Token expirado, tentando renovar com refreshToken...");

  if (!storedRefreshToken) {
    clearAllAuthData();
    setIsAuthenticated(false);
    setIsLoading(false);
    return;
  }

  try {
    const response = await axios.get(`${API_URL}/user/rtoken`, {
  headers: {
    Authorization: `Bearer ${storedRefreshToken}`,
  },
});

    const newToken = response.data?.token;
    const newRefreshToken = response.data?.refreshToken;

    if (!newToken) {
      throw new Error("Novo token não recebido");
    }

    validToken = newToken;
    localStorage.setItem("token", newToken);

    if (newRefreshToken) {
      localStorage.setItem("refreshToken", newRefreshToken);
      setRefreshToken(newRefreshToken);
    } else {
      setRefreshToken(storedRefreshToken);
    }
  } catch (error) {
    console.error("❌ Não foi possível renovar token na inicialização:", error);
    clearAllAuthData();
    setIsAuthenticated(false);
    setIsLoading(false);
    return;
  }
}

        // Tentar parsear os dados do usuário
        try {
          const userData = JSON.parse(storedUser);

          // Validar se os dados estão íntegros
          if (!userData.Nome || !userData.Role || !userData.userId) {
            throw new Error('Dados de usuário incompletos');
          }

          // Sessão restaurada de um login anterior ao campo existir: completa
          // antes de liberar, senão o escopo ficaria desconhecido até o
          // próximo login.
          await completarParceiro(userData.userId);
          const atualizado =
            JSON.parse(localStorage.getItem('user') || 'null') ?? userData;

          // Configurar estado se tudo estiver válido
          setUser(atualizado);
          setToken(validToken);
          setRefreshToken(localStorage.getItem("refreshToken"));
          setIsAuthenticated(true);

          console.log('✅ Usuário autenticado automaticamente:', userData.Nome);

        } catch (parseError) {
          console.error('❌ Erro ao analisar dados salvos:', parseError);
          clearAllAuthData();
          setIsAuthenticated(false);
        }

      } catch (error) {
        console.error('❌ Erro durante inicialização de autenticação:', error);
        clearAllAuthData();
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
        console.log('🏁 Verificação de autenticação concluída');
      }
    };

    initializeAuth();
  }, []);

  // ✅ FUNÇÃO DE LOGIN CORRIGIDA - Agora retorna dados do login
  /**
   * Completa o usuário guardado com o parceiro, quando o login não o trouxe.
   *
   * O campo existe no registro do usuário (`GET /user/:id` devolve
   * `userPartner` e `userPartnerName`), mas pode não vir na resposta do login.
   * Sem ele no localStorage, o resolvedor de escopo trata o parceiro como
   * DESCONHECIDO e — por falhar negando — bloqueia a administração de
   * parceiros.
   *
   * Roda antes de liberar a sessão, para a primeira renderização já ver o
   * escopo certo. Falha em silêncio: sem o dado, a tela de parceiros explica
   * o bloqueio em vez de conceder poder.
   */
  const completarParceiro = async (userId: number): Promise<void> => {
    try {
      const bruto = localStorage.getItem('user');
      if (!bruto) return;
      const u = JSON.parse(bruto);
      // Já veio no login — nada a fazer.
      if ('userPartner' in u) return;

      const resp = await api.get(`/user/${userId}`);
      const data = resp?.data?.user ?? resp?.data ?? {};
      // O backend ainda não tem o campo: mantém DESCONHECIDO de propósito.
      if (!('userPartner' in data)) return;

      const bruta = data.userPartner;
      u.userPartner =
        bruta === null || bruta === undefined || bruta === ''
          ? null
          : Number(bruta);
      // Nome do parceiro, quando houver — vazio conta como ausente.
      const nome = data.userPartnerName?.toString().trim();
      u.userPartnerName = nome ? nome : null;

      localStorage.setItem('user', JSON.stringify(u));
    } catch (e) {
      console.warn('Não foi possível completar o parceiro do usuário:', e);
    }
  };

  const login = async (email: string, password: string): Promise<LoginResponse> => {
    console.log('🔐 Iniciando processo de login...');

    try {
      

      const response = await api.post('user/login', { user: email, password });
      
      // ✅ CAPTURAR TODOS OS DADOS da resposta
      const loginData = response.data as LoginResponse;
      const { Nome, Role, token, refreshToken: responseRefreshToken, userId, userTenant } = loginData;
      

      // Validar resposta
      if (!token) {
        throw new Error('Token não recebido na resposta');
      }

      if (!Nome || !Role || !userId) {
        throw new Error('Dados de usuário incompletos na resposta');
      }

      // Verificar se o token recebido é válido
      if (!isTokenValid(token)) {
        throw new Error('Token recebido é inválido ou expirado');
      }

      // ✅ CORREÇÃO: Tratar refreshToken como opcional
      const safeRefreshToken = responseRefreshToken || null;

      // ✅ NOVO: Processar userTenant se disponível
      if (userTenant !== undefined && userTenant !== null) {
        console.log('🏢 userTenant encontrado no response:', userTenant);
        localStorage.setItem('selectedTenantId', String(userTenant));
      } else {
        console.log('⚠️ userTenant não encontrado no response');
        // ✅ VERIFICAR: Pode estar com nome diferente
        console.log('🔍 Verificando campos alternativos para tenant...');
        
        const possibleTenantFields = ['tenant', 'tenantId', 'tenant_id', 'associatedTenant', 'defaultTenant'];
        let tenantFound = false;
        
        for (const field of possibleTenantFields) {
          if (loginData[field] !== undefined && loginData[field] !== null) {
            
            localStorage.setItem('selectedTenantId', String(loginData[field]));
            tenantFound = true;
            break;
          }
        }
        
        if (!tenantFound) {
          console.warn('⚠️ Nenhum campo de tenant encontrado no response - usuário pode não ter acesso a tenant específico');
          // Não definir selectedTenantId - deixar a aplicação tratar
        }
      }

      // Parceiro e idioma: aceitos por vários nomes porque o backend ainda não
      // fechou o contrato. Preserva o ZERO de propósito — `|| undefined` o
      // descartaria, e zero é justamente o grupo raiz.
      const lerNumero = (...campos: string[]): number | undefined => {
        for (const campo of campos) {
          const bruto = (loginData as any)[campo];
          if (bruto === undefined || bruto === null || bruto === '') continue;
          const n = typeof bruto === 'number' ? bruto : parseInt(String(bruto), 10);
          if (!Number.isNaN(n)) return n;
        }
        return undefined;
      };
      // AUSENTE e NULO são estados diferentes, e precisam continuar diferentes:
      //  - chave ausente  → o backend ainda não informa o parceiro (DESCONHECIDO)
      //  - chave presente com null → super admin, acima de qualquer parceiro
      //
      // Tratar desconhecido como super admin concederia poder administrativo
      // por causa de um campo que faltou. Como `JSON.stringify` descarta
      // `undefined`, deixar a chave de fora preserva a distinção no
      // localStorage.
      const CHAVES_PARCEIRO = ['userPartner', 'partnerId', 'partner_id'];
      const parceiroInformado = CHAVES_PARCEIRO.some(
        (k) => k in (loginData as any),
      );
      const userPartner = parceiroInformado
        ? (lerNumero('userPartner', 'partnerId', 'partner_id') ?? null)
        : undefined;
      const userLanguage =
        (loginData as any).userLanguage ??
        (loginData as any).language ??
        null;

      // Criar objeto de usuário
      const userData: UserContext = {
        Nome,
        Role,
        userId,
        userEmail: email,
        userTenant, // ✅ ADICIONADO: Incluir userTenant no userData
        userPartner,
        userLanguage
      };

      // Aplica o idioma do usuário JÁ, com o que o próprio login devolveu.
      //
      // Sem isto a área logada é desenhada no idioma anterior e só troca
      // quando a cascata responde o GET /user/:id — e o usuário vê a interface
      // piscar de idioma. O dado já está na mão; esperar por ele de novo é
      // desperdício visível.
      //
      // Quem NÃO tem idioma próprio continua dependendo da cascata, que vai
      // buscar no haras e no parceiro.
      const idiomaDoLogin = normalizarIdioma(userLanguage);
      if (idiomaDoLogin && i18n.language !== idiomaDoLogin) {
        void i18n.changeLanguage(idiomaDoLogin);
      }

      // Salvar no localStorage ANTES de liberar a sessão: o resolvedor de
      // escopo lê daqui, e completarParceiro precisa do registro já gravado.
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', token);
      if (safeRefreshToken) {
        localStorage.setItem('refreshToken', safeRefreshToken);
      }
      localStorage.setItem('userEmail', email);

      // Busca o parceiro se o login não o trouxe, antes do primeiro render.
      await completarParceiro(userId);

      // Atualizar estado (depois do localStorage, para o escopo já estar certo)
      setUser(JSON.parse(localStorage.getItem('user') || 'null') ?? userData);
      setToken(token);
      setRefreshToken(safeRefreshToken); // ✅ CORREÇÃO: Usar versão segura
      setIsAuthenticated(true);

      // ✅ RETORNAR: Dados completos do login
      return loginData;

    } catch (error) {
      console.error('❌ Falha no login:', error);

      // Limpar qualquer dado que possa ter sido definido
      setUser(null);
      setToken(null);
      setRefreshToken(null);
      setIsAuthenticated(false);

      // Relançar erro para o componente de login
      throw error;
    }
  };

  // ✅ FUNÇÃO DE LOGOUT MELHORADA
  const logout = useCallback(() => {
    // Prevenir múltiplas chamadas simultâneas
    if (isLoggingOut.current) {
      console.log('⏸️ Logout já em andamento, ignorando...');
      return;
    }

    isLoggingOut.current = true;
    console.log('🚪 Realizando logout...');

    // Limpar estado
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    setIsAuthenticated(false);

    // Limpar todos os dados armazenados
    clearAllAuthData();

    // Limpar fila de requisições pendentes
    processQueue(new Error('Logout realizado'), null);

    console.log('✅ Logout concluído - todos os dados limpos');
    
    // Permitir novo logout após um tempo
    setTimeout(() => {
      isLoggingOut.current = false;
    }, 1000);
  }, [processQueue]);

  // Log de debug para acompanhar mudanças de estado
  // useEffect(() => {
  //   console.log('📊 Estado Auth atualizado:', {
  //     isAuthenticated,
  //     isLoading,
  //     hasUser: !!user,
  //     hasToken: !!token,
  //     hasRefreshToken: !!refreshToken,
  //     userName: user?.Nome,
  //     userTenant: user?.userTenant,
  //     tokenLength: token?.length || 0,
  //     timestamp: new Date().toISOString()
  //   });
  // }, [isAuthenticated, isLoading, user, token, refreshToken]);

  // ✅ INTERCEPTADOR DE REQUISIÇÃO MELHORADO
  useEffect(() => {
    const interceptor = api.interceptors.request.use(
      (config) => {
        // Só adicionar token se existir e não estivermos fazendo logout
        if (token && !isLoggingOut.current) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => {
      api.interceptors.request.eject(interceptor);
    };
  }, [token]);

  // ✅ INTERCEPTADOR DE RESPOSTA MELHORADO
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (originalRequest?.url?.includes("user/rtoken")) {
  console.log("🚨 Erro na rota de refresh token");
  logout();
  return Promise.reject(error);
}
        // Ignorar se já estamos fazendo logout
        if (isLoggingOut.current) {
          return Promise.reject(error);
        }

        // Se receber 401 e não for uma tentativa de refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          console.log('🚨 Token expirado (401) - tentando renovar...', {
            url: originalRequest.url,
            method: originalRequest.method
          });

          // Marcar que esta requisição já foi tentada
          originalRequest._retry = true;

          // Se já estamos fazendo refresh, adicionar à fila
          if (isRefreshing.current) {
            console.log('⏳ Refresh em andamento, adicionando à fila...');

            return new Promise((resolve, reject) => {
              failedQueue.current.push({
                resolve: (token: string) => {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  resolve(api(originalRequest));
                },
                reject: (error: any) => {
                  reject(error);
                }
              });
            });
          }

          // Iniciar processo de refresh
          isRefreshing.current = true;

          try {
            const newToken = await refreshAccessToken();

            // Processar fila de requisições pendentes
            processQueue(null, newToken);

            // Refazer a requisição original com o novo token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);

          } catch (refreshError) {
            console.error('❌ Falha ao renovar token:', refreshError);

            // Processar fila com erro
            processQueue(refreshError, null);

            // ✅ LOGOUT MAIS SEGURO: Só fazer logout se não for erro de rede
            if (refreshError instanceof Error && !refreshError.message.includes('Network')) {
              console.log('🚨 Fazendo logout devido a falha de refresh');
              logout();
            }

            return Promise.reject(refreshError);
          } finally {
            isRefreshing.current = false;
          }
        }

        // Se for erro 401 em uma tentativa de refresh, fazer logout
        if (error.response?.status === 401 && error.config.url?.includes('/user/rtoken')) {
          console.log('🚨 Refresh token inválido - fazendo logout');
          logout();
        }

        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [refreshAccessToken, processQueue, logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        refreshToken,
        isAuthenticated,
        isLoading,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;